import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';

/**
 * Per-row fill-down render state derived from the current fill-down selection box.
 *
 * These values drive how a row's cells render the fill-down box borders (see
 * {@link cellFillProps}). They are computed from `TableModel.fillDown.renderBox`,
 * which spans the rows/columns that were just filled.
 */
export interface RowFillDown {
  /**
   * The minimum column index of the fill-down box visible on this row, or null
   * when the row is outside the (edge-extended) selection box.
   */
  fillMinJ: number | null;
  /**
   * The maximum column index of the fill-down box visible on this row, or null
   * when the row is outside the (edge-extended) selection box.
   */
  fillMaxJ: number | null;
  /** Whether this row renders the top edge of the fill-down box. */
  isFillDownBoxTopEdge: boolean;
  /** Whether this row renders the bottom edge of the fill-down box. */
  isFillDownBoxBottomEdge: boolean;
  /** Whether this row is inside the (vertical span of the) fill-down box. */
  isFilledDown: boolean;
}

/**
 * Whether the row is within the fill-down selection box extended by one row on
 * each side. The box borders are drawn on both cells of each adjacent pair, so
 * the row immediately above/below the box also needs the box's column range.
 */
function isWithinFillDownBox(rowIndex: number, selectionBox: MultiSelectionBox): boolean {
  return selectionBox.minI - 1 <= rowIndex && rowIndex <= selectionBox.maxI + 1;
}

/** The minimum fill-down column index for the row, or null when outside the selection box. */
export function computeFillMinJ(rowIndex: number, selectionBox: MultiSelectionBox | null): number | null {
  if (selectionBox != null && isWithinFillDownBox(rowIndex, selectionBox)) {
    return selectionBox.minJ;
  }
  return null;
}

/** The maximum fill-down column index for the row, or null when outside the selection box. */
export function computeFillMaxJ(rowIndex: number, selectionBox: MultiSelectionBox | null): number | null {
  if (selectionBox != null && isWithinFillDownBox(rowIndex, selectionBox)) {
    return selectionBox.maxJ;
  }
  return null;
}

/** Whether this row renders the top edge of the fill-down box. */
export function computeIsFillDownBoxTopEdge(
  rowIndex: number,
  selectionBox: MultiSelectionBox | null
): boolean {
  return selectionBox != null && (rowIndex === selectionBox.minI || rowIndex === selectionBox.maxI + 1);
}

/** Whether this row renders the bottom edge of the fill-down box. */
export function computeIsFillDownBoxBottomEdge(
  rowIndex: number,
  selectionBox: MultiSelectionBox | null
): boolean {
  return selectionBox != null && (rowIndex === selectionBox.maxI || rowIndex === selectionBox.minI - 1);
}

/** Whether this row is inside the vertical span of the fill-down box. */
export function computeIsFilledDown(rowIndex: number, selectionBox: MultiSelectionBox | null): boolean {
  return selectionBox != null && selectionBox.minI <= rowIndex && rowIndex <= selectionBox.maxI;
}
